import { elementBySymbol } from '@chemai/data';
import type { MoleculeModel } from '@chemai/engine';

/**
 * "Build your own molecule": turns a 2-D sketch (atoms + bonds drawn by the student) into a
 * 3-D MoleculeModel for the existing MoleculeViewer.
 *  - Acyclic molecules are placed exactly with VSEPR templates (each atom's electron-domain
 *    geometry, chains drawn anti / zig-zag, double bonds kept planar).
 *  - Molecules with rings start from the drawing and are relaxed with simple bond / angle springs.
 * Lone pairs follow from valence electrons: (valence e⁻ − bonding e⁻) / 2 for neutral atoms.
 */

export type Vec3 = [number, number, number];
export type BondOrder = 1 | 2 | 3;
export interface SketchAtom { id: number; el: string; x: number; y: number }
export interface SketchBond { a: number; b: number; order: BondOrder }
export interface Sketch { atoms: SketchAtom[]; bonds: SketchBond[] }

export const BUILDER_ELEMENTS = ['C', 'H', 'O', 'N', 'S', 'P', 'F', 'Cl', 'Br', 'I', 'B'] as const;

const VALENCE_E: Record<string, number> = { H: 1, B: 3, C: 4, N: 5, O: 6, F: 7, P: 5, S: 6, Cl: 7, Br: 7, I: 7 };
/** Valence used when filling hydrogens automatically. */
export const TYPICAL_VALENCE: Record<string, number> = { H: 1, B: 3, C: 4, N: 3, O: 2, F: 1, P: 3, S: 2, Cl: 1, Br: 1, I: 1 };
const USUAL_MAX: Record<string, number> = { H: 1, B: 3, C: 4, N: 3, O: 2, F: 1, P: 5, S: 6, Cl: 1, Br: 1, I: 1 };
/** Pauling electronegativities. */
const EN: Record<string, number> = { H: 2.2, B: 2.04, C: 2.55, N: 3.04, O: 3.44, F: 3.98, P: 2.19, S: 2.58, Cl: 3.16, Br: 2.96, I: 2.66 };
const FALLBACK_RADIUS: Record<string, number> = { H: 31, B: 84, C: 76, N: 71, O: 66, F: 57, P: 107, S: 105, Cl: 102, Br: 120, I: 139 };

export type Shape =
  | 'linear' | 'trigonal-planar' | 'bent' | 'tetrahedral' | 'trigonal-pyramidal'
  | 'trigonal-bipyramidal' | 'seesaw' | 't-shaped' | 'octahedral' | 'square-pyramidal' | 'square-planar';

const SHAPES: Record<string, { shape: Shape; angles: number[] }> = {
  '2-0': { shape: 'linear', angles: [180] },
  '3-0': { shape: 'trigonal-planar', angles: [120] },
  '2-1': { shape: 'bent', angles: [120] },
  '4-0': { shape: 'tetrahedral', angles: [109.5] },
  '3-1': { shape: 'trigonal-pyramidal', angles: [109.5] },
  '2-2': { shape: 'bent', angles: [109.5] },
  '5-0': { shape: 'trigonal-bipyramidal', angles: [90, 120] },
  '4-1': { shape: 'seesaw', angles: [90, 120] },
  '3-2': { shape: 't-shaped', angles: [90] },
  '2-3': { shape: 'linear', angles: [180] },
  '6-0': { shape: 'octahedral', angles: [90] },
  '5-1': { shape: 'square-pyramidal', angles: [90] },
  '4-2': { shape: 'square-planar', angles: [90] },
};
const HYBRID: Record<number, string> = { 2: 'sp', 3: 'sp²', 4: 'sp³', 5: 'sp³d', 6: 'sp³d²' };

const S3 = Math.sqrt(3) / 2;
/** Domain directions by steric number; lone pairs take the first slots (equatorial / trans), as in VSEPR. */
const TEMPLATES: Record<number, Vec3[]> = {
  1: [[1, 0, 0]],
  2: [[1, 0, 0], [-1, 0, 0]],
  3: [[0, 1, 0], [S3, -0.5, 0], [-S3, -0.5, 0]],
  4: [[0, 1, 0], [0.9428, -0.3333, 0], [-0.4714, -0.3333, 0.8165], [-0.4714, -0.3333, -0.8165]],
  5: [[1, 0, 0], [-0.5, 0, S3], [-0.5, 0, -S3], [0, 1, 0], [0, -1, 0]],
  6: [[0, 1, 0], [0, -1, 0], [1, 0, 0], [-1, 0, 0], [0, 0, 1], [0, 0, -1]],
};

/* ---------- vector helpers ---------- */
const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const mul = (a: Vec3, s: number): Vec3 => [a[0] * s, a[1] * s, a[2] * s];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a: Vec3, b: Vec3): Vec3 => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const len = (a: Vec3) => Math.hypot(a[0], a[1], a[2]);
const unit = (a: Vec3): Vec3 => { const l = len(a); return l > 1e-9 ? mul(a, 1 / l) : [0, 0, 0]; };
/** Rodrigues rotation of v about a unit axis k by angle th. */
function rotate(v: Vec3, k: Vec3, th: number): Vec3 {
  const c = Math.cos(th);
  const s = Math.sin(th);
  return add(add(mul(v, c), mul(cross(k, v), s)), mul(k, dot(k, v) * (1 - c)));
}
/** Rotation taking unit vector from onto unit vector to, applied to v. */
function rotateFromTo(v: Vec3, from: Vec3, to: Vec3): Vec3 {
  const axis = cross(from, to);
  const s = len(axis);
  const c = dot(from, to);
  if (s < 1e-9) {
    if (c > 0) return v;
    const any: Vec3 = Math.abs(from[0]) < 0.9 ? [1, 0, 0] : [0, 1, 0];
    return rotate(v, unit(cross(from, any)), Math.PI);
  }
  return rotate(v, mul(axis, 1 / s), Math.atan2(s, c));
}
const reject = (v: Vec3, u: Vec3): Vec3 => sub(v, mul(u, dot(v, u)));

/** Approximate bond length (Å) from covalent radii, shortened for multiple bonds. */
function bondLength(a: string, b: string, order: number): number {
  const ra = elementBySymbol(a)?.covalentRadius ?? FALLBACK_RADIUS[a] ?? 120;
  const rb = elementBySymbol(b)?.covalentRadius ?? FALLBACK_RADIUS[b] ?? 120;
  const k = order === 3 ? 0.78 : order === 2 ? 0.87 : 1;
  return ((ra + rb) / 100) * k;
}

/* ---------- results ---------- */
export interface CenterInfo {
  atom: number;
  symbol: string;
  bonds: number;
  lonePairs: number;
  shape: Shape | null;
  angles: number[];
  hybrid: string;
}
export type BuildIssue =
  | { kind: 'overbonded' | 'steric' | 'radical' | 'unusual'; atom: number; symbol: string }
  | { kind: 'empty' | 'disconnected' };
export type BuildResult =
  | { ok: true; model: MoleculeModel; centers: CenterInfo[]; warnings: BuildIssue[] }
  | { ok: false; error: BuildIssue };

/**
 * Molecular formula with plain digits. Carbon compounds use Hill order (C, H, then alphabetical);
 * others are written the way schools write them: less electronegative element first
 * (H₂O, HCl, SO₂, SF₆, BF₃), with H last after N and P (NH₃, PH₃).
 */
export function hillFormula(symbols: string[]): string {
  const counts = new Map<string, number>();
  for (const s of symbols) counts.set(s, (counts.get(s) ?? 0) + 1);
  const keys = [...counts.keys()];
  let order: string[];
  if (counts.has('C')) {
    order = ['C', ...(counts.has('H') ? ['H'] : []), ...keys.filter((k) => k !== 'C' && k !== 'H').sort()];
  } else {
    order = [...keys].sort((a, b) => (EN[a] ?? 2.5) - (EN[b] ?? 2.5) || a.localeCompare(b));
    if (counts.has('H') && (counts.has('N') || counts.has('P'))) order = [...order.filter((k) => k !== 'H'), 'H'];
  }
  return order.map((k) => `${k}${counts.get(k)! > 1 ? counts.get(k) : ''}`).join('');
}

function hash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return (h >>> 0).toString(36);
}

export function buildModel(sk: Sketch): BuildResult {
  const n = sk.atoms.length;
  if (n === 0) return { ok: false, error: { kind: 'empty' } };
  const idx = new Map(sk.atoms.map((a, i) => [a.id, i]));
  const sym = sk.atoms.map((a) => a.el);
  const nbrs: Array<Array<{ j: number; order: number }>> = sk.atoms.map(() => []);
  const bonds: Array<{ a: number; b: number; order: number }> = [];
  for (const b of sk.bonds) {
    const i = idx.get(b.a);
    const j = idx.get(b.b);
    if (i === undefined || j === undefined || i === j) continue;
    nbrs[i].push({ j, order: b.order });
    nbrs[j].push({ j: i, order: b.order });
    bonds.push({ a: i, b: j, order: b.order });
  }

  // connectivity
  const seen = new Set([0]);
  const stack = [0];
  while (stack.length) for (const { j } of nbrs[stack.pop()!]) if (!seen.has(j)) { seen.add(j); stack.push(j); }
  if (seen.size < n) return { ok: false, error: { kind: 'disconnected' } };

  const warnings: BuildIssue[] = [];
  const lp: number[] = [];
  const sn: number[] = [];
  for (let i = 0; i < n; i++) {
    const bsum = nbrs[i].reduce((s, x) => s + x.order, 0);
    const ve = VALENCE_E[sym[i]] ?? 4;
    if (bsum > ve) return { ok: false, error: { kind: 'overbonded', atom: i, symbol: sym[i] } };
    const free = ve - bsum;
    lp.push(Math.floor(free / 2));
    if (free % 2 === 1) warnings.push({ kind: 'radical', atom: i, symbol: sym[i] });
    if (bsum > (USUAL_MAX[sym[i]] ?? 4)) warnings.push({ kind: 'unusual', atom: i, symbol: sym[i] });
    sn.push(nbrs[i].length + lp[i]);
    if (sn[i] > 6) return { ok: false, error: { kind: 'steric', atom: i, symbol: sym[i] } };
  }

  const pos: Vec3[] = sk.atoms.map(() => [0, 0, 0]);
  const lpDirs: Vec3[][] = sk.atoms.map(() => []);
  const cyclic = bonds.length >= n;

  if (!cyclic) {
    // ---- exact VSEPR placement over the bond tree ----
    let root = 0;
    for (let i = 1; i < n; i++) if (nbrs[i].length > nbrs[root].length) root = i;
    const dirsOf: Vec3[][] = sk.atoms.map(() => []);
    const placed = new Set<number>([root]);
    const queue: Array<{ i: number; parent: number }> = [{ i: root, parent: -1 }];
    while (queue.length) {
      const { i, parent } = queue.shift()!;
      let D: Vec3[] = (TEMPLATES[sn[i]] ?? []).map((d) => [...d] as Vec3);
      const k = lp[i];
      if (parent >= 0 && D.length) {
        const u = unit(sub(pos[i], pos[parent]));
        const back = mul(u, -1);
        const from = unit(D[k]);
        D = D.map((d) => rotateFromTo(d, from, back));
        // twist about the bond so substituents sit anti to the parent's (zig-zag chains, planar C=C)
        const ref = dirsOf[parent].find((d) => dot(unit(d), u) < 0.9);
        const cand = D[k + 1] ?? D[0];
        if (ref && cand && D.length > 1) {
          const rp = unit(reject(ref, u));
          const cp = unit(reject(cand, u));
          if (len(rp) > 0.5 && len(cp) > 0.5) {
            const target = mul(rp, -1);
            const th = Math.atan2(dot(cross(cp, target), u), dot(cp, target));
            D = D.map((d) => rotate(d, u, th));
          }
        }
      }
      dirsOf[i] = D;
      lpDirs[i] = D.slice(0, k);
      const slots = D.slice(k);
      let s = parent >= 0 ? 1 : 0;
      for (const { j, order } of nbrs[i]) {
        if (j === parent || placed.has(j)) continue;
        const dir = unit(slots[s++] ?? [1, 0, 0]);
        pos[j] = add(pos[i], mul(dir, bondLength(sym[i], sym[j], order)));
        placed.add(j);
        queue.push({ i: j, parent: i });
      }
    }
  } else {
    // ---- rings: start from the drawing, relax bonds and angles ----
    let mean = 0;
    for (const b of bonds) mean += Math.hypot(sk.atoms[b.a].x - sk.atoms[b.b].x, sk.atoms[b.a].y - sk.atoms[b.b].y);
    mean /= bonds.length;
    const scale = mean > 1e-6 ? 1.45 / mean : 0.02;
    const cx = sk.atoms.reduce((s, a) => s + a.x, 0) / n;
    const cy = sk.atoms.reduce((s, a) => s + a.y, 0) / n;
    sk.atoms.forEach((a, i) => { pos[i] = [(a.x - cx) * scale, -(a.y - cy) * scale, 0.3 * Math.sin(i * 2.3 + 1)]; });

    const springs: Array<{ a: number; b: number; d: number; k: number }> = [];
    const linked = new Set<string>();
    const key = (a: number, b: number) => (a < b ? `${a}-${b}` : `${b}-${a}`);
    for (const b of bonds) { springs.push({ a: b.a, b: b.b, d: bondLength(sym[b.a], sym[b.b], b.order), k: 0.5 }); linked.add(key(b.a, b.b)); }
    const IDEAL: Record<number, number> = { 2: 180, 3: 120, 4: 109.47 };
    for (let c = 0; c < n; c++) {
      const th = IDEAL[sn[c]];
      if (!th || nbrs[c].length < 2) continue;
      const cos = Math.cos((th * Math.PI) / 180);
      for (let p = 0; p < nbrs[c].length; p++) for (let q = p + 1; q < nbrs[c].length; q++) {
        const A = nbrs[c][p];
        const B = nbrs[c][q];
        const la = bondLength(sym[c], sym[A.j], A.order);
        const lb = bondLength(sym[c], sym[B.j], B.order);
        springs.push({ a: A.j, b: B.j, d: Math.sqrt(la * la + lb * lb - 2 * la * lb * cos), k: 0.25 });
        linked.add(key(A.j, B.j));
      }
    }
    for (let it = 0; it < 900; it++) {
      for (const s of springs) {
        const dv = sub(pos[s.b], pos[s.a]);
        const d = len(dv) || 1e-6;
        const corr = mul(dv, ((d - s.d) / d) * s.k * 0.5);
        pos[s.a] = add(pos[s.a], corr);
        pos[s.b] = sub(pos[s.b], corr);
      }
      for (let a = 0; a < n; a++) for (let b = a + 1; b < n; b++) {
        if (linked.has(key(a, b))) continue;
        const dv = sub(pos[b], pos[a]);
        const d = len(dv) || 1e-6;
        if (d < 2.3) {
          const corr = mul(dv, ((2.3 - d) / d) * 0.05);
          pos[a] = sub(pos[a], corr);
          pos[b] = add(pos[b], corr);
        }
      }
    }
    // lone pairs from the relaxed bond directions
    for (let i = 0; i < n; i++) {
      if (lp[i] === 0 || nbrs[i].length < 2) continue;
      const us = nbrs[i].map(({ j }) => unit(sub(pos[j], pos[i])));
      const sum = us.reduce((s, u) => add(s, u), [0, 0, 0] as Vec3);
      if (len(sum) < 0.1) continue;
      const o = unit(mul(sum, -1));
      if (lp[i] === 1) lpDirs[i] = [o];
      else if (lp[i] === 2 && us.length === 2) {
        const nn = unit(cross(us[0], us[1]));
        const a = (54.74 * Math.PI) / 180;
        lpDirs[i] = [unit(add(mul(o, Math.cos(a)), mul(nn, Math.sin(a)))), unit(sub(mul(o, Math.cos(a)), mul(nn, Math.sin(a))))];
      }
    }
  }

  // centre the molecule
  const cen = mul(pos.reduce((s, p) => add(s, p), [0, 0, 0] as Vec3), 1 / n);
  for (let i = 0; i < n; i++) pos[i] = sub(pos[i], cen);

  // polarity: sum of bond dipoles (ΔEN ≥ 0.4) pointing to the more electronegative atom
  let dip: Vec3 = [0, 0, 0];
  for (const b of bonds) {
    const d = (EN[sym[b.b]] ?? 2.5) - (EN[sym[b.a]] ?? 2.5);
    if (Math.abs(d) < 0.4) continue;
    dip = add(dip, mul(unit(sub(pos[b.b], pos[b.a])), d));
  }
  const polar = len(dip) > 0.3;

  let sigma = 0;
  let pi = 0;
  for (const b of bonds) { sigma += 1; pi += b.order - 1; }

  const centers: CenterInfo[] = [];
  for (let i = 0; i < n; i++) {
    if (nbrs[i].length < 2) continue;
    const row = SHAPES[`${nbrs[i].length}-${lp[i]}`];
    centers.push({ atom: i, symbol: sym[i], bonds: nbrs[i].length, lonePairs: lp[i], shape: row?.shape ?? null, angles: row?.angles ?? [], hybrid: HYBRID[sn[i]] ?? '' });
  }

  const formula = hillFormula(sym);
  const lonePairs: Array<{ atom: number; direction: Vec3 }> = [];
  for (let i = 0; i < n; i++) if (nbrs[i].length >= 2) for (const d of lpDirs[i]) lonePairs.push({ atom: i, direction: d });

  const model: MoleculeModel = {
    id: `custom-${hash(JSON.stringify(sk))}`,
    formula,
    atoms: sym.map((s, i) => ({ symbol: s, position: pos[i] })),
    bonds,
    lonePairs,
    vsepr: null,
    polar,
    sigmaBonds: sigma,
    piBonds: pi,
  };
  return { ok: true, model, centers, warnings };
}

/** Adds hydrogens (in 2-D, in the free directions) until every heavy atom reaches its usual valence. */
export function fillHydrogens(sk: Sketch): Sketch {
  let nextId = Math.max(0, ...sk.atoms.map((a) => a.id)) + 1;
  const atoms = [...sk.atoms];
  const bonds = [...sk.bonds];
  for (const at of sk.atoms) {
    if (at.el === 'H') continue;
    const mine = bonds.filter((b) => b.a === at.id || b.b === at.id);
    const missing = (TYPICAL_VALENCE[at.el] ?? 4) - mine.reduce((s, b) => s + b.order, 0);
    if (missing <= 0) continue;
    const used = mine.map((b) => {
      const o = atoms.find((x) => x.id === (b.a === at.id ? b.b : b.a))!;
      return Math.atan2(o.y - at.y, o.x - at.x);
    });
    for (let h = 0; h < missing; h++) {
      let best = 0;
      let bestGap = -1;
      for (let s = 0; s < 72; s++) {
        const ang = (s * Math.PI) / 36;
        const gap = used.length ? Math.min(...used.map((u) => Math.abs(Math.atan2(Math.sin(ang - u), Math.cos(ang - u))))) : Math.PI - Math.abs(ang - Math.PI);
        if (gap > bestGap + 1e-6) { bestGap = gap; best = ang; }
      }
      used.push(best);
      const id = nextId++;
      atoms.push({ id, el: 'H', x: at.x + 46 * Math.cos(best), y: at.y + 46 * Math.sin(best) });
      bonds.push({ a: at.id, b: id, order: 1 });
    }
  }
  return { atoms, bonds };
}

export function removeHydrogens(sk: Sketch): Sketch {
  const hs = new Set(sk.atoms.filter((a) => a.el === 'H').map((a) => a.id));
  if (hs.size === sk.atoms.length) return sk;
  return { atoms: sk.atoms.filter((a) => !hs.has(a.id)), bonds: sk.bonds.filter((b) => !hs.has(b.a) && !hs.has(b.b)) };
}

/* ---------- starter drawings ---------- */
function chain(spec: Array<[string, number, number]>, links: Array<[number, number, BondOrder]>): Sketch {
  return {
    atoms: spec.map(([el, x, y], i) => ({ id: i + 1, el, x, y })),
    bonds: links.map(([a, b, order]) => ({ a: a + 1, b: b + 1, order })),
  };
}
function ring(el: string, cx: number, cy: number, r: number, alternate: boolean): Sketch {
  const spec: Array<[string, number, number]> = [];
  const links: Array<[number, number, BondOrder]> = [];
  for (let i = 0; i < 6; i++) {
    const t = (i * Math.PI) / 3 - Math.PI / 2;
    spec.push([el, cx + r * Math.cos(t), cy + r * Math.sin(t)]);
    links.push([i, (i + 1) % 6, alternate && i % 2 === 0 ? 2 : 1]);
  }
  return chain(spec, links);
}

export const STARTERS: Record<string, () => Sketch> = {
  methane: () => fillHydrogens(chain([['C', 300, 180]], [])),
  ammonia: () => fillHydrogens(chain([['N', 300, 180]], [])),
  ethene: () => fillHydrogens(chain([['C', 260, 180], ['C', 340, 180]], [[0, 1, 2]])),
  ethyne: () => fillHydrogens(chain([['C', 260, 180], ['C', 340, 180]], [[0, 1, 3]])),
  ethanol: () => fillHydrogens(chain([['C', 220, 200], ['C', 290, 160], ['O', 360, 200]], [[0, 1, 1], [1, 2, 1]])),
  aceticAcid: () => fillHydrogens(chain([['C', 230, 200], ['C', 300, 160], ['O', 300, 90], ['O', 370, 200]], [[0, 1, 1], [1, 2, 2], [1, 3, 1]])),
  benzene: () => fillHydrogens(ring('C', 300, 180, 62, true)),
  cyclohexane: () => fillHydrogens(ring('C', 300, 180, 62, false)),
};
